// À La Carte Order Reducer

import * as actionTypes from './constants';

const TRANSPORTATION_FEE = 39.00;

const initialState = {
  currentStep: 0,
  boxCount: 0,
  boxProduct: null,
  packingSupplies: [],
  movingSupplies: [],
  deliveryExtraWork: null, // {id, title, price} for delivery
  pickupExtraWork: null, // {id, title, price} for pickup
  orderId: null, // Store order ID to prevent duplicate order creation
  deliveryDetails: {
    delivery_date: null,
    delivery_window_1_id: null,
    delivery_window_2_id: null,
    delivery_address: null,
    apt_number: null,
    extra_work_id: null,
    description: null
  },
  pickupDetails: {
    pickup_date: null,
    pickup_window_1_id: null,
    pickup_window_2_id: null,
    pickup_address: null,
    apt_number: null,
    extra_work_id: null,
    description: null
  },
  lockedPickupDetails: null,
  deliveryAddressFromRental: null,
  customerInfo: null,
  rentalInfo: {
    rentalPeriod: null,  // e.g., "2 Weeks"
    rentalWeeks: 0,       // e.g., 2
    orderId: null,
    parentPickupDate: null,  // Parent rental's pickup date
    parentDeliveryDate: null  // Parent rental's delivery date
  },
  taxRate: 0, // Tax rate from backend (percentage)
  pricing: {
    boxesSubtotal: 0,
    suppliesSubtotal: 0,
    extraWorkPrice: 0,
    subtotal: 0,
    transportationFee: TRANSPORTATION_FEE,
    tax: 0,
    total: 0,
    remainingWeeks: 0,
    boxPricePerWeek: 0
  }
};

const calculatePricing = (state) => {
  const boxPricePerWeek = parseFloat(state.boxProduct?.alacarte_price || state.boxProduct?.price || 0) || 0;

  let remainingWeeks = state.rentalInfo.rentalWeeks || 1;

  const effectiveDeliveryDate = state.deliveryDetails.delivery_date || state.rentalInfo.parentDeliveryDate;

  if (state.rentalInfo.parentPickupDate && effectiveDeliveryDate) {
    try {
      const pickupDate = new Date(state.rentalInfo.parentPickupDate);
      const deliveryDate = new Date(effectiveDeliveryDate);

      const remainingDays = Math.ceil((pickupDate - deliveryDate) / (1000 * 60 * 60 * 24));

      console.log('=== À La Carte Remaining Weeks Calculation ===');
      console.log('Parent pickup date:', state.rentalInfo.parentPickupDate, '→', pickupDate.toDateString());
      console.log('Effective delivery date:', effectiveDeliveryDate, '→', deliveryDate.toDateString());
      console.log('Using:', state.deliveryDetails.delivery_date ? 'à la carte delivery date' : 'parent rental delivery date (default)');
      console.log('Remaining days:', remainingDays);
      console.log('Remaining weeks (rounded up):', Math.ceil(remainingDays / 7));

      if (remainingDays > 0) {
        remainingWeeks = Math.ceil(remainingDays / 7);
      } else {
        remainingWeeks = 1;
      }
    } catch (error) {
      console.error('Error calculating remaining weeks:', error);
      remainingWeeks = state.rentalInfo.rentalWeeks || 1;
    }
  } else {
    console.warn('Cannot calculate remaining weeks - using fallback:', {
      parentPickupDate: state.rentalInfo.parentPickupDate,
      alacarteDeliveryDate: state.deliveryDetails.delivery_date,
      parentDeliveryDate: state.rentalInfo.parentDeliveryDate,
      fallbackWeeks: remainingWeeks
    });
  }

  // Calculate boxes subtotal with pro-rated weekly pricing
  const boxesSubtotal = state.boxCount * boxPricePerWeek * remainingWeeks;

  const packingSuppliesSubtotal = state.packingSupplies.reduce((total, supply) => {
    return total + (supply.price * supply.quantity);
  }, 0);

  const movingSuppliesSubtotal = state.movingSupplies.reduce((total, supply) => {
    return total + (supply.price * supply.quantity * remainingWeeks);
  }, 0);

  const deliveryExtraWorkPrice = state.deliveryExtraWork ? parseFloat(state.deliveryExtraWork.price) : 0;
  const pickupExtraWorkPrice = state.pickupExtraWork ? parseFloat(state.pickupExtraWork.price) : 0;
  const extraWorkPrice = deliveryExtraWorkPrice + pickupExtraWorkPrice;

  const suppliesSubtotal = packingSuppliesSubtotal + movingSuppliesSubtotal;

  const subtotal = boxesSubtotal + suppliesSubtotal + extraWorkPrice;

  const hasItems = state.boxCount > 0 || state.packingSupplies.length > 0 || state.movingSupplies.length > 0;
  const transportationFee = hasItems ? TRANSPORTATION_FEE : 0;

  const tax = ((state.taxRate * packingSuppliesSubtotal) / 100);
  const roundedTax = (Math.round(tax * 100) / 100);

  // Total
  const total = subtotal + transportationFee + roundedTax;

  return {
    boxesSubtotal: parseFloat(boxesSubtotal.toFixed(2)),
    suppliesSubtotal: parseFloat(suppliesSubtotal.toFixed(2)),
    extraWorkPrice: parseFloat(extraWorkPrice.toFixed(2)),
    subtotal: parseFloat(subtotal.toFixed(2)),
    transportationFee: parseFloat(transportationFee.toFixed(2)),
    tax: parseFloat(roundedTax.toFixed(2)),
    total: parseFloat(total.toFixed(2)),
    remainingWeeks: remainingWeeks,  // Include for display purposes
    boxPricePerWeek: parseFloat(boxPricePerWeek) || 0  // Ensure it's always a number
  };
};

const alaCarteReducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.SET_ALACARTE_STEP:
      return {
        ...state,
        currentStep: action.payload
      };

    case actionTypes.SET_BOX_COUNT: {
      const newStateWithBoxes = {
        ...state,
        boxCount: action.payload
      };
      return {
        ...newStateWithBoxes,
        pricing: calculatePricing(newStateWithBoxes)
      };
    }

    case actionTypes.SET_BOX_PRODUCT: {
      const newStateWithProduct = {
        ...state,
        boxProduct: action.payload
      };
      return {
        ...newStateWithProduct,
        pricing: calculatePricing(newStateWithProduct)
      };
    }

    case actionTypes.SET_PACKING_SUPPLIES: {
      const newStateWithPacking = {
        ...state,
        packingSupplies: action.payload
      };
      return {
        ...newStateWithPacking,
        pricing: calculatePricing(newStateWithPacking)
      };
    }

    case actionTypes.SET_MOVING_SUPPLIES: {
      const newStateWithMoving = {
        ...state,
        movingSupplies: action.payload
      };
      return {
        ...newStateWithMoving,
        pricing: calculatePricing(newStateWithMoving)
      };
    }

    case actionTypes.ADD_PACKING_SUPPLY: {
      const newStateAddPacking = {
        ...state,
        packingSupplies: [...state.packingSupplies, action.payload]
      };
      return {
        ...newStateAddPacking,
        pricing: calculatePricing(newStateAddPacking)
      };
    }

    case actionTypes.REMOVE_PACKING_SUPPLY: {
      const newStateRemovePacking = {
        ...state,
        packingSupplies: state.packingSupplies.filter(s => s.id !== action.payload)
      };
      return {
        ...newStateRemovePacking,
        pricing: calculatePricing(newStateRemovePacking)
      };
    }

    case actionTypes.UPDATE_PACKING_SUPPLY_QUANTITY: {
      const newStateUpdatePacking = {
        ...state,
        packingSupplies: state.packingSupplies.map(s =>
          s.id === action.payload.supplyId
            ? { ...s, quantity: action.payload.quantity }
            : s
        )
      };
      return {
        ...newStateUpdatePacking,
        pricing: calculatePricing(newStateUpdatePacking)
      };
    }

    case actionTypes.ADD_MOVING_SUPPLY: {
      const newStateAddMoving = {
        ...state,
        movingSupplies: [...state.movingSupplies, action.payload]
      };
      return {
        ...newStateAddMoving,
        pricing: calculatePricing(newStateAddMoving)
      };
    }

    case actionTypes.REMOVE_MOVING_SUPPLY: {
      const newStateRemoveMoving = {
        ...state,
        movingSupplies: state.movingSupplies.filter(s => s.id !== action.payload)
      };
      return {
        ...newStateRemoveMoving,
        pricing: calculatePricing(newStateRemoveMoving)
      };
    }

    case actionTypes.UPDATE_MOVING_SUPPLY_QUANTITY: {
      const newStateUpdateMoving = {
        ...state,
        movingSupplies: state.movingSupplies.map(s =>
          s.id === action.payload.supplyId
            ? { ...s, quantity: action.payload.quantity }
            : s
        )
      };
      return {
        ...newStateUpdateMoving,
        pricing: calculatePricing(newStateUpdateMoving)
      };
    }

    case actionTypes.SET_DELIVERY_DETAILS: {
      const newStateWithDelivery = {
        ...state,
        deliveryDetails: {
          ...state.deliveryDetails,
          ...action.payload
        }
      };
      // Recalculate pricing when delivery date changes (affects remaining weeks calculation)
      return {
        ...newStateWithDelivery,
        pricing: calculatePricing(newStateWithDelivery)
      };
    }

    case actionTypes.SET_PICKUP_DETAILS:
      return {
        ...state,
        pickupDetails: {
          ...state.pickupDetails,
          ...action.payload
        }
      };

    case actionTypes.SET_CUSTOMER_INFO:
      return {
        ...state,
        customerInfo: action.payload
      };

    case actionTypes.SET_RENTAL_INFO: {
      const newStateWithRentalInfo = {
        ...state,
        rentalInfo: {
          rentalPeriod: action.payload.rental_period,
          rentalWeeks: action.payload.rental_weeks,
          orderId: action.payload.order_id,
          parentPickupDate: action.payload.pickup_details?.pickup_date || null,
          parentDeliveryDate: action.payload.delivery_date || null
        }
      };
      // Recalculate pricing when rental info changes (includes parent pickup date)
      return {
        ...newStateWithRentalInfo,
        pricing: calculatePricing(newStateWithRentalInfo)
      };
    }

    case actionTypes.SET_LOCKED_PICKUP_DETAILS:
      return {
        ...state,
        lockedPickupDetails: action.payload
      };

    case actionTypes.SET_DELIVERY_ADDRESS_FROM_RENTAL:
      return {
        ...state,
        deliveryAddressFromRental: action.payload
      };

    case actionTypes.SET_TAX_RATE: {
      const newStateWithTax = {
        ...state,
        taxRate: action.payload
      };
      return {
        ...newStateWithTax,
        pricing: calculatePricing(newStateWithTax)
      };
    }

    case actionTypes.SET_EXTRA_WORK: {
      const newStateWithExtraWork = {
        ...state,
        extraWork: action.payload
      };
      return {
        ...newStateWithExtraWork,
        pricing: calculatePricing(newStateWithExtraWork)
      };
    }

    case actionTypes.SET_DELIVERY_EXTRA_WORK: {
      const newStateWithDeliveryExtraWork = {
        ...state,
        deliveryExtraWork: action.payload
      };
      return {
        ...newStateWithDeliveryExtraWork,
        pricing: calculatePricing(newStateWithDeliveryExtraWork)
      };
    }

    case actionTypes.SET_PICKUP_EXTRA_WORK: {
      const newStateWithPickupExtraWork = {
        ...state,
        pickupExtraWork: action.payload
      };
      return {
        ...newStateWithPickupExtraWork,
        pricing: calculatePricing(newStateWithPickupExtraWork)
      };
    }

    case actionTypes.SET_ORDER_ID:
      return {
        ...state,
        orderId: action.payload
      };

    case actionTypes.CALCULATE_TOTAL:
      return {
        ...state,
        pricing: calculatePricing(state)
      };

    case actionTypes.RESET_ALACARTE_STORE:
      return initialState;

    default:
      return state;
  }
};

export default alaCarteReducer;